import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Alert,
  InputAdornment,
  TextField,
  Autocomplete,
  TablePagination,
} from "@mui/material";
import { CirclePlus, Search, Calculator, Cog } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "../../components/shared/modals/DeleteConfirmationModal";
import {
  getProjections,
  deleteProjection,
  calculateProjection,
  generateProjection,
  manualCreateProjection,
  manualEditProjection,
} from "../../api/Projection/api";
import usePermissions from "./hooks/usePermissions";
import DataTable from "../../components/DataTable";
import ProjectionForm from "../../components/shared/forms/ProjectionForm";
import { AuthContext } from "../../contexts/AuthContext";
import { dukaElements } from "../../api/Duka/api";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 0,
};

function getDayName(dateString) {
  const date = new Date(dateString);
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return dayNames[date.getDay()];
}

const transformCalculatedProjection = (proj) => {
  const dayColumns = {
    monday: "-",
    tuesday: "-",
    wednesday: "-",
    thursday: "-",
    friday: "-",
  };

  if (proj.sales_details && Array.isArray(proj.sales_details)) {
    proj.sales_details.forEach((detail) => {
      const dayName = getDayName(detail.sale_date);
      const lowerDay = dayName.toLowerCase();
      if (dayColumns.hasOwnProperty(lowerDay)) {
        dayColumns[lowerDay] = detail.amount;
      }
    });
  }

  return {
    id: proj.id,
    duka: proj.duka_name,
    product: proj.product_name,
    forecastDate: proj.forecast_date,
    forecastQuantity: proj.forecasted_quantity,
    proposalReason: proj.proposal_reason || "N/A",
    finalQuantity: proj.final_quantity || "-",
    status: proj.status || "DRAFT",
    monday: dayColumns.monday,
    tuesday: dayColumns.tuesday,
    wednesday: dayColumns.wednesday,
    thursday: dayColumns.thursday,
    friday: dayColumns.friday,
    original: proj,
  };
};

const Projection = () => {
  const {
    loading: loadingPermissions,
    error: permissionsError,
    can,
  } = usePermissions();
  const { user } = useContext(AuthContext);
  const [projection, setProjection] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedProjection, setSelectedProjection] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectionToDelete, setProjectionToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showCalculateBar, setShowCalculateBar] = useState(false);
  const [barMode, setBarMode] = useState(null);
  const [calculateStartDate, setCalculateStartDate] = useState("");
  const [calculateEndDate, setCalculateEndDate] = useState("");
  const [selectedDuka, setSelectedDuka] = useState(null);
  const [dukases, setDukases] = useState([]);
  const [isTemporaryProjection, setIsTemporaryProjection] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!loadingPermissions && can.viewProjection()) {
      fetchProjection();
    }
  }, [loadingPermissions, page, rowsPerPage, searchQuery]);

  useEffect(() => {
    const fetchDukas = async () => {
      try {
        const response = await dukaElements("dukas");
        setDukases(response.data);
      } catch (error) {
        console.error("Error fetching dukas:", error);
      }
    };

    fetchDukas();
  }, []);

  const fetchProjection = async () => {
    try {
      setLoading(true);
      const response = await getProjections({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
      });

      if (response?.data) {
        const transformedData = response.data.map((proj) =>
          transformCalculatedProjection(proj)
        );
        console.log("Projection loaded:", transformedData);
        setProjection(transformedData);
        setTotal(response.total);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching projection:", err);
      setError("Failed to load projection. Please try again later.");
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProjection = async (data) => {
    try {
      await manualCreateProjection(data);
      setOpenModal(false);
      fetchProjection();
    } catch (error) {
      console.error("Error creating projection:", error);
      setError("Failed to create projection: " + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateProjection = async (id, data) => {
    try {
      await manualEditProjection(id, data);
      setOpenModal(false);
      setSelectedProjection(null);
      fetchProjection();
    } catch (error) {
      console.error("Error updating projection:", error);
      setError("Failed to update projection: " + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteProjection = async () => {
    if (!projectionToDelete) return;

    try {
      await deleteProjection(projectionToDelete.id);
      setDeleteModalOpen(false);
      setProjectionToDelete(null);
      fetchProjection();
    } catch (error) {
      console.error("Error deleting projection:", error);
      setError("Failed to delete projection: " + (error.response?.data?.error || error.message));
    }
  };

  const handleSubmit = (data) => {
    if (selectedProjection) {
      handleUpdateProjection(selectedProjection.id, data);
    } else {
      handleCreateProjection(data);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCalculateSubmit = async () => {
    try {
      const response = await calculateProjection({
        duka_id: parseInt(selectedDuka?.id),
        start_date: calculateStartDate,
        end_date: calculateEndDate,
      });

      const calculatedResults = response.data.map((proj) =>
        transformCalculatedProjection(proj)
      );
      setProjection(calculatedResults);
      setIsTemporaryProjection(true);
      setShowCalculateBar(false);
    } catch (error) {
      console.error("Error calculating projection:", error);
      setError("Failed to calculate projection: " + (error.response?.data?.error || error.message));
    }
  };

  const handleGenerateSubmit = async () => {
    try {
      const response = await generateProjection({
        duka_id: parseInt(selectedDuka?.id),
        start_date: calculateStartDate,
        end_date: calculateEndDate,
      });
      console.log("Generated Projection:", response.data);
      await fetchProjection();
      setShowCalculateBar(false);
    } catch (error) {
      console.error("Error generating projection:", error);
      setError("Failed to generate projection: " + (error.response?.data?.error || error.message));
    }
  };

  const filteredProjection = projection.filter((proj) =>
    Object.values(proj).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const columns = [
    { id: "duka", label: "Duka" },
    { id: "product", label: "Product" },
    { id: "forecastDate", label: "Forecast Date" },
    { id: "forecastQuantity", label: "Forecast Quantity" },
    { id: "finalQuantity", label: "Final Quantity" },
    { id: "status", label: "Status" },
    { id: "proposalReason", label: "Proposal Reason" },
    { id: "monday", label: "Monday" },
    { id: "tuesday", label: "Tuesday" },
    { id: "wednesday", label: "Wednesday" },
    { id: "thursday", label: "Thursday" },
    { id: "friday", label: "Friday" },
  ];

  const isActionDisabled = !selectedDuka || !calculateStartDate || !calculateEndDate;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ color: "#124F3E", fontWeight: "bold", mb: 3 }}
      >
        Manage Projection
      </Typography>

      {(error || permissionsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || permissionsError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          {can.createProjection() && (
            <Button
              variant="contained"
              startIcon={<Calculator size={18} />}
              onClick={() => {
                if (isTemporaryProjection) {
                  fetchProjection();
                  setIsTemporaryProjection(false);
                  setShowCalculateBar(false);
                }
                setBarMode("calculate");
                setShowCalculateBar(true);
              }}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": { backgroundColor: "#1a614d !important" },
              }}
            >
              Calculate Projection
            </Button>
          )}
          {can.createProjection() && (
            <Button
              variant="contained"
              startIcon={<Cog size={18} />}
              onClick={() => {
                setBarMode("generate");
                setShowCalculateBar(true);
              }}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": { backgroundColor: "#1a614d !important" },
              }}
            >
              Generate Projection
            </Button>
          )}
          {can.createProjection() && (
            <Button
              variant="contained"
              startIcon={<CirclePlus size={18} />}
              onClick={() => {
                setSelectedProjection(null);
                setOpenModal(true);
              }}
              sx={{
                fontWeight: "bold",
                bgcolor: "#124F3E",
                "&:hover": { backgroundColor: "#1a614d !important" },
              }}
            >
              Add Projection
            </Button>
          )}
        </Box>
       </Box>

      {showCalculateBar && (
        <Box
          sx={{
            width: "100%",
            mb: 3,
            p: 2,
            border: "1px solid #ccc",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Autocomplete
            sx={{ flexGrow: 1 }}
            options={dukases}
            getOptionLabel={(option) => option.name || ""}
            value={selectedDuka}
            onChange={(_, newValue) => setSelectedDuka(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Duka"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          <TextField
            type="date"
            label="Start Date"
            value={calculateStartDate}
            onChange={(e) => setCalculateStartDate(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="End Date"
            value={calculateStartDate}
            onChange={(e) => setCalculateEndDate(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            onClick={() => {
              if (barMode === "calculate") {
                handleCalculateSubmit();
              } else if (barMode === "generate") {
                handleGenerateSubmit();
              }
            }}
            disabled={isActionDisabled}
            sx={{
              fontWeight: "bold",
              bgcolor: "#124F3E",
              "&:hover": { backgroundColor: "#1a614d !important" },
            }}
          >
            {barMode === "calculate" ? "Calculate" : "Generate"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setShowCalculateBar(false);
              setSelectedDuka(null);
              setCalculateStartDate("");
              setCalculateEndDate("");
            }}
            sx={{
              fontWeight: "bold",
              borderColor: "#124F3E",
              color: "#124F3E",
              "&:hover": { backgroundColor: "#e0e0e0" },
            }}
          >
            Cancel
          </Button>
        </Box>
      )}

      {!showCalculateBar && (
        <DataTable
          loading={loading || loadingPermissions}
          columns={columns}
          rows={filteredProjection}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onEdit={
            can.editProjection()
              ? (rowData) => {
                  setSelectedProjection(rowData.original);
                  setOpenModal(true);
                }
              : undefined
          }
          onDelete={
            can.deleteProjection()
              ? (rowData) => {
                  setProjectionToDelete(rowData);
                  setDeleteModalOpen(true);
                }
              : undefined
          }
          editIcon={<EditIcon />}
          deleteIcon={<DeleteIcon />}
          editTooltip="Edit"
          deleteTooltip="Delete"
          editDisabled={!can.editProjection()}
          deleteDisabled={!can.deleteProjection()}
          totalRows={total}
          components={{
            Pagination: () => (
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            ),
          }}
        />
      )}

      {(can.createProjection() || can.editProjection()) && (
        <Modal
          open={openModal}
          onClose={() => {
            setSelectedProjection(null);
            setOpenModal(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <ProjectionForm
              initialData={selectedProjection}
              onSubmit={handleSubmit}
              onCancel={() => {
                setSelectedProjection(null);
                setOpenModal(false);
              }}
            />
          </Box>
        </Modal>
      )}

      {can.deleteProjection() && (
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setProjectionToDelete(null);
          }}
          onConfirm={handleDeleteProjection}
          title="Delete Projection"
          message="Are you sure you want to delete this Projection?"
          itemName={projectionToDelete?.duka}
        />
      )}
    </Box>
  );
};

export default Projection;